import React, { useState } from 'react';
import {
  useChannelsQuery,
  useChannelDetailQuery,
  usePopularAssetsQuery,
} from 'src/api/queries/deliveryAnalyticsQueries';
import { DeliveryAnalyticsFilters } from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import AssetsTable from '../components/AssetsTable';
import { AsyncSection, DataAsOfBadge, Section } from '../components/common';
import { channelLabel, UNTAGGED_CHANNEL } from '../utils/channelLabels';
import styles from '../styles.module.scss';

const MiniTable: React.FC<{
  title: string;
  rows: { dimension: string; requests: number }[];
  format?: (dimension: string) => string;
}> = ({ title, rows, format }) => (
  <div className={styles.miniTable}>
    <h4>{title}</h4>
    {rows.length === 0 ? (
      <p className={styles.miniEmpty}>—</p>
    ) : (
      <ul>
        {rows.slice(0, 5).map((r) => (
          <li key={r.dimension || '(empty)'}>
            <span>{format ? format(r.dimension) : r.dimension || '(unknown)'}</span>
            <span>{formatNumberIntl(r.requests)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ChannelDetail: React.FC<{
  channelKey: string;
  filters: DeliveryAnalyticsFilters;
  onClose: () => void;
}> = ({ channelKey, filters, onClose }) => {
  const { data, isLoading, error } = useChannelDetailQuery(channelKey, filters);
  const [viewAll, setViewAll] = useState(false);

  // Lazy full-asset list for this channel, only fetched when "View all" is on.
  // Reuses the popular-assets endpoint scoped by channel_key (no 10-asset cap;
  // limit is the backend's 1000 ceiling).
  const all = usePopularAssetsQuery(
    { ...filters, channelKey, metric: 'requests', limit: 1000 },
    { enabled: viewAll },
  );

  const topAssets = data?.top_assets ?? [];
  const distinctAssets = data?.totals.distinct_assets ?? 0;
  const canViewAll = distinctAssets > topAssets.length;
  const assetRows = viewAll ? all.data?.data ?? [] : topAssets;
  const isUntagged = channelKey === UNTAGGED_CHANNEL;

  return (
    <Section
      title={isUntagged ? 'Untagged traffic (no channel)' : `Channel: ${channelLabel(channelKey)}`}
      headerRight={
        <button type="button" className={styles.clearButton} onClick={onClose}>
          Close
        </button>
      }
    >
      <AsyncSection isLoading={isLoading} error={error} isEmpty={!data}>
        {data && (
          <>
            <div className={styles.statGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatNumberIntl(data.totals.requests)}</div>
                <div className={styles.statLabel}>Requests</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatBillingBytes(data.totals.bytes)}</div>
                <div className={styles.statLabel}>Bytes</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {formatNumberIntl(data.totals.distinct_assets)}
                </div>
                <div className={styles.statLabel}>Distinct assets</div>
              </div>
            </div>

            <div className={styles.assetsBlock}>
              <div className={styles.assetsHeader}>
                <h4>{viewAll ? 'All assets' : 'Top assets'}</h4>
                {canViewAll && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => setViewAll((v) => !v)}
                  >
                    {viewAll
                      ? `Show top ${topAssets.length}`
                      : `View all ${formatNumberIntl(distinctAssets)} assets`}
                  </button>
                )}
              </div>
              <AsyncSection
                isLoading={viewAll && all.isLoading}
                error={viewAll ? all.error : null}
                isEmpty={assetRows.length === 0}
                emptyLabel={
                  isUntagged
                    ? 'No untagged deliveries in the selected range.'
                    : 'No assets for this channel in the selected range.'
                }
              >
                <AssetsTable rows={assetRows} showActions />
              </AsyncSection>
            </div>

            <div className={styles.miniTableGrid}>
              <MiniTable title="By campaign" rows={data.campaign_breakdown} />
              <MiniTable title="By country" rows={data.audience_by_country} />
              <MiniTable title="By device" rows={data.audience_by_device} />
              <MiniTable title="Top transforms" rows={data.top_transforms} />
              <MiniTable title="By placement" rows={data.placement_breakdown} />
              <MiniTable title="By variant" rows={data.variant_breakdown} />
            </div>
          </>
        )}
      </AsyncSection>
    </Section>
  );
};

/** "Utilized by channel": where declared (Marketo, YouTube, newsletter, …) the
 *  asset deliveries were attributed. Distinct from the observed delivery domains
 *  in the Audience → Referrer view. Select a channel to drill in. */
const ChannelsTab: React.FC<{ filters: DeliveryAnalyticsFilters }> = ({ filters }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading, error } = useChannelsQuery({ ...filters, limit: 100 });
  const rows = data?.data ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.requests), 0);

  return (
    <>
      <Section
        title="Utilized by channel"
        description="Delivery volume grouped by the channel each request declared (fs_channel, falling back to utm_source). Untagged traffic is grouped separately. Select a channel to drill in."
        headerRight={<DataAsOfBadge value={data?.data_as_of} loading={isLoading} />}
      >
        <AsyncSection isLoading={isLoading} error={error} isEmpty={rows.length === 0}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Channel</th>
                <th className={styles.numCol}>Requests</th>
                <th className={styles.numCol}>Bytes</th>
                <th className={styles.numCol}>Share</th>
                <th className={styles.barCol}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.channel || '(empty)'}
                  className={styles.clickableRow}
                  onClick={() => setSelected(r.channel)}
                >
                  <td>{channelLabel(r.channel)}</td>
                  <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
                  <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
                  <td className={styles.numCol}>{(r.share * 100).toFixed(1)}%</td>
                  <td className={styles.barCol}>
                    <span
                      className={styles.bar}
                      style={{ width: max > 0 ? `${(r.requests / max) * 100}%` : '0%' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AsyncSection>
      </Section>

      {selected && (
        <ChannelDetail
          channelKey={selected}
          filters={filters}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default ChannelsTab;
